import { useEffect, useRef, useState } from "react";
import { Check, ChevronUp, Mic, Video, Volume2 } from "lucide-react";
import { mediaDeviceService } from "../../services/media/device.service";
import { subscribeToMediaDeviceChange } from "../../utils/mediaDeviceChange";

type DeviceKind = "camera" | "microphone" | "speaker";

interface DevicePickerProps {
  kind: DeviceKind;
  selectedId?: string;
  onSelect: (device: MediaDeviceInfo) => void;
  direction?: "up" | "down";
  variant?: "default" | "segmented";
  menuPosition?: "trigger" | "viewport-bottom";
}

const fallbackLabel = (kind: DeviceKind, index: number) =>
  `${
    kind === "camera"
      ? "Camera"
      : kind === "microphone"
        ? "Microphone"
        : "Speaker"
  } ${index + 1}`;

const DevicePicker = ({
  kind,
  selectedId,
  onSelect,
  direction = "up",
  variant = "default",
  menuPosition = "trigger",
}: DevicePickerProps) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  const load = async () => {
    const list =
      kind === "camera"
        ? await mediaDeviceService.cameras()
        : kind === "microphone"
          ? await mediaDeviceService.microphones()
          : await mediaDeviceService.speakers();
    setDevices(list);
  };

  useEffect(() => {
    void load();
    return subscribeToMediaDeviceChange(() => void load());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, []);

  const activeId = selectedId || devices[0]?.deviceId;
  const isSegmented = variant === "segmented";
  const DeviceIcon =
    kind === "camera" ? Video : kind === "microphone" ? Mic : Volume2;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={`Choose ${kind}`}
        aria-expanded={open}
        className={
          isSegmented
            ? "flex h-10 w-9 items-center justify-center rounded-r-full border-l border-white/20 bg-blue-700 text-white transition hover:bg-blue-800"
            : "flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-white transition hover:bg-slate-600"
        }
      >
        <ChevronUp
          className={`h-4 w-4 transition-transform ${
            open ? "" : direction === "up" ? "" : "rotate-180"
          }`}
        />
      </button>

      {open && (
        <div
          className={`z-50 overflow-y-auto rounded-xl border border-slate-200 bg-white py-2 shadow-2xl ${
            menuPosition === "viewport-bottom"
              ? "fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-1/2 max-h-[calc(100dvh-7rem-env(safe-area-inset-bottom))] w-[min(420px,calc(100vw-2rem))] -translate-x-1/2"
              : `absolute right-0 ${isSegmented ? "w-[420px]" : "w-64"} ${direction === "up" ? "bottom-12" : "top-12"}`
          }`}
        >
          {devices.length === 0 && (
            <p className="px-4 py-3 text-sm text-slate-400">No {kind} found</p>
          )}
          {devices.map((device, index) => {
            const isActive = device.deviceId === activeId;
            return (
              <button
                key={device.deviceId || index}
                type="button"
                onClick={() => {
                  onSelect(device);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition hover:bg-slate-50 ${
                  isActive ? "font-semibold text-slate-900" : "text-slate-600"
                }`}
              >
                <Check
                  className={`h-4 w-4 shrink-0 text-blue-600 ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                />
                <DeviceIcon className="h-4 w-4 shrink-0 text-slate-600" />
                <span className="min-w-0 flex-1 truncate">
                  {device.label || fallbackLabel(kind, index)}
                </span>
                {isActive && (
                  <span className="shrink-0 text-xs font-medium text-slate-400">
                    System default
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DevicePicker;
